/**
 * BasisTheory tokenization provider implementation
 */

import { BasisTheory } from '@basis-theory/basis-theory-js';

import { CardPaymentMethod, ApplePayToken, GooglePayToken, PaymentSDKError } from '../types';
import { getCardMonthAndYear, validateCardNumber, validateExpiryDate, validateCVC } from '../utils';
import { ITokenizationProvider, RawTokenResponse, ProviderConfig } from './types';

export class BasisTheoryProvider implements ITokenizationProvider {
    private basisTheory: BasisTheory | null = null;
    private initialized = false;
    private config: ProviderConfig;

    constructor(config: ProviderConfig) {
        this.config = config;
    }

    getProviderName(): string {
        return 'BasisTheory';
    }

    async initialize(): Promise<void> {
        if (this.initialized && this.basisTheory) {
            return;
        }

        try {
            const bt = new BasisTheory();
            await bt.init(this.config.apiKey);
            this.basisTheory = bt;
            this.initialized = true;
            console.log('✅ BasisTheory provider initialized');
        } catch (error) {
            console.error('BasisTheory initialization error:', error);
            throw new PaymentSDKError(
                'Failed to initialize BasisTheory provider',
                'PROVIDER_INITIALIZATION_ERROR',
                error,
            );
        }
    }

    isInitialized(): boolean {
        return this.initialized && this.basisTheory !== null;
    }

    private validateCardData(cardData: CardPaymentMethod): void {
        if (!validateCardNumber(cardData.cardNumber)) {
            throw new PaymentSDKError('Invalid card number format', 'VALIDATION_ERROR');
        }

        if (!validateExpiryDate(cardData.expiryDate)) {
            throw new PaymentSDKError('Invalid or expired expiry date', 'VALIDATION_ERROR');
        }

        if (!validateCVC(cardData.cvc)) {
            throw new PaymentSDKError('Invalid CVC format', 'VALIDATION_ERROR');
        }
    }

    async tokenizeCard(cardData: CardPaymentMethod): Promise<RawTokenResponse> {
        if (!this.isInitialized() || !this.basisTheory) {
            await this.initialize();
        }

        if (!this.basisTheory) {
            throw new PaymentSDKError('BasisTheory not initialized', 'PROVIDER_NOT_INITIALIZED');
        }

        this.validateCardData(cardData);

        try {
            console.log('🔐 BasisTheory: Tokenizing card');

            const btResponse = await this.basisTheory.tokens.create({
                type: 'card',
                data: {
                    cvc: cardData.cvc,
                    number: Number(cardData.cardNumber.replace(/\s+/g, '')),
                    ...getCardMonthAndYear(cardData.expiryDate),
                },
                ...(cardData.cardholderName
                    ? { metadata: { cardholderName: cardData.cardholderName } }
                    : {}),
            });

            console.log('✅ BasisTheory: Card tokenized:', btResponse.id);
            return btResponse as RawTokenResponse;
        } catch (error) {
            console.error('❌ BasisTheory tokenization error:', error);
            throw new PaymentSDKError('Failed to tokenize card with BasisTheory', 'TOKENIZATION_ERROR', error);
        }
    }

    async tokenizeApplePay(applePayToken: ApplePayToken): Promise<RawTokenResponse> {
        if (!applePayToken) {
            throw new PaymentSDKError('Apple Pay token is missing', 'VALIDATION_ERROR');
        }

        try {
            console.log('🔐 BasisTheory: Tokenizing Apple Pay');

            // Use BasisTheory's Apple Pay API endpoint
            const response = await fetch('https://api.basistheory.com/apple-pay', {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'BT-API-KEY': this.config.apiKey,
                },
                body: JSON.stringify({
                    apple_payment_data: applePayToken,
                }),
            });

            if (!response.ok) {
                const errorData = await response.json().catch(() => ({}));
                console.error('BasisTheory Apple Pay API error:', errorData);
                throw new Error(`HTTP error! Status: ${response.status}`);
            }

            const jsonResponse = await response.json();
            console.log('✅ BasisTheory: Apple Pay tokenized:', jsonResponse.token_intent?.id || jsonResponse.apple_pay?.id);

            // Return normalized response
            const tokenResponse: RawTokenResponse = {
                id: jsonResponse.token_intent?.id || jsonResponse.apple_pay?.id || jsonResponse.id,
                type: 'apple_pay',
                data: applePayToken,
                metadata: jsonResponse,
            };

            return tokenResponse;
        } catch (error) {
            console.error('❌ BasisTheory Apple Pay error:', error);
            throw new PaymentSDKError('Failed to tokenize Apple Pay with BasisTheory', 'TOKENIZATION_ERROR', error);
        }
    }

    async tokenizeGooglePay(googlePayToken: GooglePayToken): Promise<RawTokenResponse> {
        if (!googlePayToken.signedMessage) {
            throw new PaymentSDKError('Google Pay token is missing', 'VALIDATION_ERROR');
        }

        try {
            console.log('🔐 BasisTheory: Tokenizing Google Pay');

            // Use BasisTheory's Google Pay API endpoint
            const response = await fetch('https://api.basistheory.com/google-pay', {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'BT-API-KEY': this.config.apiKey,
                },
                body: JSON.stringify({
                    google_payment_data: googlePayToken,
                }),
            });

            if (!response.ok) {
                const errorData = await response.json().catch(() => ({}));
                console.error('BasisTheory Google Pay API error:', errorData);
                throw new Error(`HTTP error! Status: ${response.status}`);
            }

            const jsonResponse = await response.json();
          return jsonResponse?.token_intent || jsonResponse?.google_pay;
        } catch (error) {
            console.error('❌ BasisTheory Google Pay error:', error);
            throw new PaymentSDKError('Failed to tokenize Google Pay with BasisTheory', 'TOKENIZATION_ERROR', error);
        }
    }

    async validateApplePayMerchant(displayName: string, domain: string): Promise<any> {
        try {
            console.log('🔐 BasisTheory: Validating Apple Pay merchant');

            const response = await fetch('https://api.basistheory.com/apple-pay/session', {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'BT-API-KEY': this.config.apiKey,
                },
                body: JSON.stringify({
                    display_name: displayName,
                    domain: domain,
                }),
            });

            if (!response.ok) {
                const errorData = await response.json().catch(() => ({}));
                console.error('BasisTheory Apple Pay merchant validation error:', errorData);
                throw new Error(`HTTP error! Status: ${response.status}`);
            }

            const merchantSession = await response.json();
            console.log('✅ BasisTheory: Apple Pay merchant validated');
            return merchantSession;
        } catch (error) {
            console.error('❌ BasisTheory Apple Pay merchant validation error:', error);
            throw new PaymentSDKError('Failed to validate Apple Pay merchant with BasisTheory', 'MERCHANT_VALIDATION_ERROR', error);
        }
    }

    detectScaRequirement(rawResponse: RawTokenResponse): boolean {
        // BasisTheory: Check card.authentication field
        if (rawResponse.card?.authentication === 'sca_required') {
            return true;
        }
        return false;
    }
}

